import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { Alert, Box, Button, Grid, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { ClockHeader } from '../../../ui-component/header/ClockHeader';
import MainCard from '../../../ui-component/cards/MainCard';
import { FilterBar } from '../../../ui-component/production/ProductionCard';
import CustomWrapper from '../CustomWrapper';
import editRequestService from '../../../services/editRequestService';
import productionService from '../../../services/productionService';
import { useSelector } from 'react-redux';

export const traducciones = {
    name: 'es',
    options: {
        months: [
            'Enero',
            'Febrero',
            'Marzo',
            'Abril',
            'Mayo',
            'Junio',
            'Julio',
            'Agosto',
            'Septiembre',
            'Octubre',
            'Noviembre',
            'Diciembre'
        ],
        shortMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
        days: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
        shortDays: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
        toolbar: {
            exportToSVG: 'Descargar SVG',
            exportToPNG: 'Descargar PNG',
            exportToCSV: 'Descargar CSV',
            menu: 'Menú de descargas',
            selection: 'Selección',
            selectionZoom: 'Zoom de selección',
            zoomIn: 'Acercar',
            zoomOut: 'Alejar',
            pan: 'Mover',
            reset: 'Restablecer Zoom'
        }
    }
};

let CHARTS = {
    NONE: null,
    DEFECTS: 'DEFECTS',
    GOAL: 'GOAL',
    TIME_CYCLE: 'TIME_CYCLE',
    DOWNTIME: 'DOWNTIME',
    YIELD: 'YIELD'
};

const ProductionRecordsPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dateFilter, setDateFilter] = useState(productionService.formatDateISO(new Date()));
    const [machineFilter, setMachineFilter] = useState('all');
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [activeShift, setActiveShift] = useState(null);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError('');
        editRequestService
            .listEditRequests(dateFilter)
            .then((data) => {
                if (isMounted) setRecords(data);
            })
            .catch((err) => {
                if (isMounted) {
                    setError(err?.response?.data?.message || 'No fue posible cargar los registros de producción.');
                }
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, [dateFilter]);

    const machineOptions = useMemo(() => ['all', ...Array.from(new Set(records.map((r) => r.machine)))], [records]);

    const filteredRecords = useMemo(
        () => (machineFilter === 'all' ? records : records.filter((r) => r.machine === machineFilter)),
        [records, machineFilter]
    );

    const handleToggleRecord = (record) => {
        setSelectedRecordId((prev) => (prev === record.id ? null : record.id));
    };

    return (
        <CustomWrapper>
            <Box sx={{ minHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <ClockHeader showButton={true} buttonAction={() => navigate('/')} />
                </Box>

                {/*PRODUCTION RECORDS*/}
                <MainCard
                    content={false}
                    sx={{
                        width: '100%',
                        maxWidth: '95%',
                        mx: 'auto',
                        borderRadius: 4,
                        boxShadow: 4
                    }}
                >
                    <Box sx={{ backgroundColor: '#fff', px: 2, py: 1, flexDirection: 'row', display: 'flex', gap: 1 }}>
                        <Typography variant="h5">Estadísticas </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 400, display: 'inline', color: theme.palette.primary.main }}>
                            Planta {customization.userData?.factory?.name} - Turno{' '}
                            {activeShift?.name || customization.userData?.shift || 'No disponible.'}
                        </Typography>
                    </Box>

                    <Box sx={{ p: 0, height: '65vh', display: 'flex', flexDirection: 'column' }}>
                        <FilterBar>
                            <Typography variant="h5" sx={{ mb: 1, color: 'white' }}>
                                Filtros
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <OutlinedInput
                                        type="date"
                                        fullWidth
                                        value={dateFilter}
                                        onChange={(e) => setDateFilter(e.target.value)}
                                        startAdornment={
                                            <CalendarMonthIcon
                                                sx={{
                                                    mr: 1,
                                                    fontSize: '1.1rem',
                                                    color: theme.palette.secondary.dark
                                                }}
                                            />
                                        }
                                        sx={{
                                            backgroundColor: alpha(theme.palette.primary.main, 0.8),
                                            borderRadius: 3,
                                            '& .MuiOutlinedInput-input': {
                                                color: theme.palette.secondary.dark,
                                                fontSize: 15
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #A9D550' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { border: '2px solid #A9D550' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '2px solid #A9D550' }
                                        }}
                                    />
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => setDateFilter(productionService.formatDateISO(new Date()))}
                                        sx={{
                                            height: '100%',
                                            borderRadius: 3,
                                            fontWeight: 700,
                                            color: theme.palette.secondary.dark
                                        }}
                                    >
                                        Hoy
                                    </Button>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <Select
                                        fullWidth
                                        value={machineFilter}
                                        onChange={(e) => setMachineFilter(e.target.value)}
                                        displayEmpty
                                        input={
                                            <OutlinedInput
                                                sx={{
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.8),
                                                    borderRadius: 3,
                                                    '& .MuiSelect-select': {
                                                        backgroundColor: 'transparent',
                                                        color: theme.palette.secondary.dark,
                                                        fontSize: 15
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #A9D550' },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { border: '2px solid #A9D550' },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '2px solid #A9D550' }
                                                }}
                                            />
                                        }
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme.palette.secondary.dark,
                                                    color: 'black',
                                                    mt: 1,
                                                    borderRadius: 3
                                                }
                                            }
                                        }}
                                    >
                                        {machineOptions.map((machine) => (
                                            <MenuItem key={machine} value={machine}>
                                                {machine === 'all' ? 'Todas las máquinas' : machine}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            </Grid>
                        </FilterBar>

                        <Box sx={{ p: 6, overflowY: 'auto' }}>
                            {error ? <Alert severity="error">{error}</Alert> : null}
                            {!loading && !error && filteredRecords.length === 0 ? (
                                <Alert severity="info">No hay registros para la fecha seleccionada.</Alert>
                            ) : null}
                            {filteredRecords.map((record) => (
                                <EditRequestCard
                                    key={record.id}
                                    request={record}
                                    isSelected={selectedRecordId === record.id}
                                    onSelect={handleToggleRecord}
                                    previewGoodQty={record.goodPieces}
                                    previewDefectiveQty={record.defectivePieces}
                                    handleOpenDistribution={() => {}}
                                    previewCicle={record.timeCicle}
                                />
                            ))}
                        </Box>
                    </Box>
                </MainCard>
            </Box>
        </CustomWrapper>
    );
};

export default ProductionRecordsPage;
